import { useState, useEffect, useCallback } from 'react';
import distributorService from '../services/distributorService';
import { UX_TIMING, OPERATION_STATUS } from '../constants';

/**
 * Hook personalizado para gestión completa de distribuidores
 * @returns {Object} - Estado y funciones para gestionar distribuidores
 */
export const useDistributors = () => {
    // Estados principales
    const [distributors, setDistributors] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        total_contacts_assigned: 0,
        total_contacts_unassigned: 0,
        total_revenue: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estados de paginación
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0,
        hasNext: false,
        hasPrev: false
    });

    // Estados de operaciones
    const [operationStatus, setOperationStatus] = useState(null);
    const [operatingDistributorName, setOperatingDistributorName] = useState('');
    const [isEditingMode, setIsEditingMode] = useState(false);
    const [operationError, setOperationError] = useState(null);

    // Estados para dashboard
    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [dashboardError, setDashboardError] = useState(null);

    // Estados para distribuidores activos (para selección)
    const [activeDistributors, setActiveDistributors] = useState([]);
    const [activeDistributorsLoading, setActiveDistributorsLoading] = useState(false);

    /**
     * Carga distribuidores y estadísticas desde el backend con paginación
     */
    const loadDistributors = useCallback(async (page = 1, limit = 10, options = {}) => {
        try {
            setLoading(true);
            setError(null);

            const requestOptions = {
                page,
                limit,
                ...options
            };

            const [distributorsResponse, statsResponse] = await Promise.all([
                distributorService.getAllDistributors(requestOptions),
                distributorService.getDistributorStats()
            ]);

            if (distributorsResponse.success) {
                const distributorsData = distributorsResponse.data?.distributors || distributorsResponse.data || [];
                setDistributors(distributorsData);

                if (distributorsResponse.data?.pagination) {
                    setPagination(distributorsResponse.data.pagination);
                }
            }

            if (statsResponse.success) {
                const statsData = statsResponse.data || {
                    total: 0,
                    active: 0,
                    inactive: 0,
                    total_contacts_assigned: 0,
                    total_contacts_unassigned: 0,
                    total_revenue: 0
                };
                setStats(statsData);
            }

        } catch (err) {
            setError(err.message || 'Error cargando los datos de distribuidores');
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Carga distribuidores activos para selección
     */
    const loadActiveDistributors = useCallback(async () => {
        try {
            setActiveDistributorsLoading(true);
            const response = await distributorService.getActiveDistributors();

            if (response.success) {
                setActiveDistributors(response.data || []);
            }
        } catch (err) {
            console.error('Error cargando distribuidores activos:', err);
        } finally {
            setActiveDistributorsLoading(false);
        }
    }, []);

    /**
     * Carga dashboard de un distribuidor específico
     */
    const loadDistributorDashboard = useCallback(async (distributorId, options = {}) => {
        try {
            setDashboardLoading(true);
            setDashboardError(null);

            const response = await distributorService.getDistributorDashboard(distributorId, options);

            if (response.success) {
                setDashboardData(response.data);
            } else {
                setDashboardError(response.message || 'Error cargando dashboard del distribuidor');
            }
        } catch (err) {
            setDashboardError(err.message || 'Error cargando dashboard del distribuidor');
        } finally {
            setDashboardLoading(false);
        }
    }, []);

    /**
     * Cambiar a una página específica
     */
    const goToPage = useCallback(async (page) => {
        await loadDistributors(page, pagination.limit);
    }, [loadDistributors, pagination.limit]);

    /**
     * Ir a la página siguiente
     */
    const nextPage = useCallback(async () => {
        if (pagination.hasNext) {
            await goToPage(pagination.page + 1);
        }
    }, [pagination.hasNext, pagination.page, goToPage]);

    /**
     * Ir a la página anterior
     */
    const prevPage = useCallback(async () => {
        if (pagination.hasPrev) {
            await goToPage(pagination.page - 1);
        }
    }, [pagination.hasPrev, pagination.page, goToPage]);

    /**
     * Cambiar el número de elementos por página
     */
    const changePageSize = useCallback(async (newLimit) => {
        await loadDistributors(1, newLimit); // Reiniciar a página 1 con nuevo tamaño
    }, [loadDistributors]);

    /**
     * Ejecuta una operación de distribuidor con UX optimizada
     */
    const executeDistributorOperation = useCallback(async (operation, distributorData, isEdit = false) => {
        setOperatingDistributorName(distributorData.nombre_empresa || 'Distribuidor');
        setOperationStatus(OPERATION_STATUS.LOADING);
        setIsEditingMode(isEdit);

        const startTime = Date.now();

        try {
            const response = await operation();

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);

            if (remainingTime > 0) {
                await new Promise(resolve => setTimeout(resolve, remainingTime));
            }

            if (response.success) {
                setOperationStatus(OPERATION_STATUS.SUCCESS);
                await loadDistributors(pagination.page, pagination.limit);
                return { success: true };
            } else {
                setOperationStatus(OPERATION_STATUS.ERROR);
                setOperationError(response.message || 'Ocurrió un error inesperado.');
                return { success: false, message: response.message };
            }

        } catch (error) {
            console.error('Error en operación de distribuidor:', error);
            setOperationStatus(OPERATION_STATUS.ERROR);
            setOperationError(error.message || 'No se pudo conectar con el servidor.');
            return { success: false, message: error.message };
        }
    }, [loadDistributors, pagination.page, pagination.limit]);

    /**
     * Crea un nuevo distribuidor
     */
    const createDistributor = useCallback(async (distributorData) => {
        return executeDistributorOperation(
            () => distributorService.createDistributor(distributorData),
            distributorData,
            false
        );
    }, [executeDistributorOperation]);

    /**
     * Actualiza un distribuidor existente
     */
    const updateDistributor = useCallback(async (distributorId, distributorData) => {
        return executeDistributorOperation(
            () => distributorService.updateDistributor(distributorId, distributorData),
            distributorData,
            true
        );
    }, [executeDistributorOperation]);

    /**
     * Elimina un distribuidor
     */
    const deleteDistributor = useCallback(async (distributorId, distributorData = { nombre_empresa: 'Distribuidor' }) => {
        return executeDistributorOperation(
            () => distributorService.deleteDistributor(distributorId),
            distributorData,
            false
        );
    }, [executeDistributorOperation]);

    /**
     * Activa/desactiva un distribuidor
     */
    const toggleDistributorStatus = useCallback(async (distributorId, isActive, distributorData) => {
        return executeDistributorOperation(
            () => distributorService.toggleDistributorStatus(distributorId, isActive),
            distributorData,
            true
        );
    }, [executeDistributorOperation]);

    /**
     * Busca distribuidores con filtros
     */
    const searchDistributors = useCallback(async (searchTerm, activeOnly = false) => {
        await loadDistributors(1, pagination.limit, {
            search: searchTerm,
            activeOnly
        });
    }, [loadDistributors, pagination.limit]);

    /**
     * Resetea el estado de operación
     */
    const resetOperationState = useCallback(() => {
        setOperationStatus(OPERATION_STATUS.IDLE);
        setOperatingDistributorName('');
        setIsEditingMode(false);
        setOperationError(null);
    }, []);

    /**
     * Resetea el estado del dashboard
     */
    const resetDashboardState = useCallback(() => {
        setDashboardData(null);
        setDashboardError(null);
    }, []);

    // Cargar datos al montar el componente
    useEffect(() => {
        loadDistributors();
        loadActiveDistributors();
    }, [loadDistributors, loadActiveDistributors]);

    return {
        // Estados principales
        distributors,
        stats,
        loading,
        error,
        pagination,

        // Estados de operaciones
        operationStatus,
        operatingDistributorName,
        isEditingMode,
        operationError,

        // Estados de dashboard
        dashboardData,
        dashboardLoading,
        dashboardError,

        // Estados de distribuidores activos
        activeDistributors,
        activeDistributorsLoading,

        // Funciones principales
        loadDistributors,
        createDistributor,
        updateDistributor,
        deleteDistributor,
        toggleDistributorStatus,
        searchDistributors,

        // Funciones de paginación
        goToPage,
        nextPage,
        prevPage,
        changePageSize,

        // Funciones de dashboard
        loadDistributorDashboard,
        resetDashboardState,

        // Funciones de utilidad
        loadActiveDistributors,
        resetOperationState,
    };
};